import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Loader2, Menu, RotateCw, GitBranch, ChevronDown, Check, X } from 'lucide-react'
import {
  ensureWorkspace,
  restartAppWorkspaceContainer,
  getApp,
  listAppSessions,
  getImportStatus,
  getImportTimeline,
  type Workspace as WorkspaceInfo,
  type AppSession,
  type ImportStatus,
  type ImportTimelineEvent,
} from '../lib/api'
import PanelToggle, { PanelType } from '../components/workspace/PanelToggle'
import WorkspaceLayout from '../components/workspace/WorkspaceLayout'
import MobileWorkspaceLayout from '../components/workspace/MobileWorkspaceLayout'
import { useIsMobile } from '../hooks/useMediaQuery'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'
import HelpDrawer from '../components/HelpDrawer'
import BetaBadge from '../components/BetaBadge'

// Available panels constant (docs added conditionally inside component based on appName)

// Storage keys for panel preferences (different for app vs general workspace)
const PANEL_PREFS_KEY = 'workspace-panel-prefs'
const APP_PANEL_PREFS_KEY = 'workspace-app-panel-prefs'

function loadPanelPrefs(hasApp: boolean): PanelType[] {
  const storageKey = hasApp ? APP_PANEL_PREFS_KEY : PANEL_PREFS_KEY
  try {
    const stored = localStorage.getItem(storageKey)
    if (stored) {
      const parsed = JSON.parse(stored)
      // Validate that parsed value is an array (old format was different)
      if (Array.isArray(parsed)) {
        return parsed
      }
      // Clear old/invalid format
      localStorage.removeItem(storageKey)
    }
  } catch (e) {
    // Clear corrupted data
    localStorage.removeItem(storageKey)
  }
  // Default panels based on context:
  // - App workspace: Preview panel (local iframe browser)
  // - General workspace: Server Browser panel (Xpra)
  return hasApp ? ['preview'] : ['browser']
}

function savePanelPrefs(prefs: PanelType[], hasApp: boolean) {
  const storageKey = hasApp ? APP_PANEL_PREFS_KEY : PANEL_PREFS_KEY
  try {
    localStorage.setItem(storageKey, JSON.stringify(prefs))
  } catch (e) {
    // Ignore
  }
}

// Session/branch dropdown for switching between sessions in the workspace toolbar
function SessionDropdown({
  appName,
  activeSession,
  onSelectSession,
}: {
  appName: string
  activeSession: string
  onSelectSession: (sessionName: string) => void
}) {
  const [sessions, setSessions] = useState<AppSession[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Fetch sessions on mount and every 30 seconds
  useEffect(() => {
    const load = async () => {
      try {
        const result = await listAppSessions(appName)
        setSessions(result.sessions)
      } catch {
        // Ignore fetch errors silently
      }
    }
    load()
    intervalRef.current = setInterval(load, 30000)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [appName])

  const activeSessions = sessions.filter(s => s.status !== 'archived')

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors px-1.5 py-0.5 rounded hover:bg-secondary"
      >
        <GitBranch className="w-3.5 h-3.5" />
        <span>{activeSession}</span>
        <ChevronDown className="w-3 h-3" />
      </button>
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-0 top-full mt-1 z-50 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[200px]">
            {activeSessions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">No sessions</div>
            ) : (
              activeSessions.map(session => (
                <button
                  key={session.id}
                  onClick={() => {
                    onSelectSession(session.name)
                    setIsOpen(false)
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-secondary transition-colors flex items-center gap-2"
                >
                  <GitBranch className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">{session.name}</span>
                  {session.name === activeSession && (
                    <Check className="w-3.5 h-3.5 text-primary ml-auto flex-shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default function Workspace() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  // Get folder parameter for initial working directory
  const folder = searchParams.get('folder')
  // Get app URL for browser panel
  const appUrl = searchParams.get('url')
  // Get app name (either explicit param or extracted from folder path)
  const appNameParam = searchParams.get('app')
  // Extract app name from folder path like /home/coder/workspace/apps/{name}
  const extractedAppName = folder?.match(/(?:\/home\/coder\/)?workspace\/apps\/([^/]+)/)?.[1]
  const appName = appNameParam || extractedAppName
  // Optional import id for showing background import progress after redirect
  const importId = useMemo(() => {
    const raw = searchParams.get('import_id')
    if (!raw) return null
    const parsed = Number(raw)
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null
  }, [searchParams])
  // Get session parameter (contains terminal URL with tmux session arg)
  // e.g., "/workspace/app/myapp/terminal/?arg=main"
  const sessionParam = searchParams.get('session')
  // Extract the tmux session name from the session URL's arg parameter
  const tmuxSession = sessionParam ? new URLSearchParams(sessionParam.split('?')[1] || '').get('arg') : undefined

  // Session state: activeSession tracks the currently displayed session,
  // requestedSession is the "intent" that TerminalTabs consumes to switch tabs
  const [activeSession, setActiveSession] = useState(tmuxSession || 'main')
  const [requestedSession, setRequestedSession] = useState<string | undefined>(undefined)

  // Handle session selection from dropdown
  const handleSelectSession = useCallback((sessionName: string) => {
    setRequestedSession(sessionName)
    setActiveSession(sessionName)
  }, [])

  // When TerminalTabs acknowledges a session change via onSessionChange callback
  const handleSessionChange = useCallback((sessionName: string) => {
    setActiveSession(sessionName)
  }, [])

  // Workspace state
  const [workspaceStatus, setWorkspaceStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [, setWorkspace] = useState<WorkspaceInfo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [importProgress, setImportProgress] = useState<ImportStatus | null>(null)
  const [, setImportEvents] = useState<ImportTimelineEvent[]>([])
  const [dismissedImportBannerId, setDismissedImportBannerId] = useState<number | null>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [restarting, setRestarting] = useState(false)

  // Compute effective preview URL based on active session:
  // - main session: use the app's deployed URL (appUrl from search params)
  // - non-main sessions: use branch preview URL (/a/branch/{sessionName}/{appName}/)
  const effectivePreviewUrl = useMemo(() => {
    if (!appUrl) return undefined

    let resolvedUrl = appUrl
    if (activeSession && activeSession !== 'main') {
      try {
        const parsed = new URL(appUrl)
        resolvedUrl = `${parsed.origin}/a/branch/${activeSession}/${appName}/`
      } catch {
        resolvedUrl = appUrl
      }
    }

    // Force a one-time iframe reload when import reaches complete so preview
    // switches off the temporary "Importing..." page without manual refresh.
    if (importId && importProgress?.status === 'complete') {
      try {
        const parsed = new URL(resolvedUrl)
        parsed.searchParams.set('cc_import_complete', importProgress.updated_at || String(importId))
        return parsed.toString()
      } catch {
        return resolvedUrl
      }
    }

    return resolvedUrl
  }, [appUrl, appName, activeSession, importId, importProgress?.status, importProgress?.updated_at])

  // Panel preferences (context-aware based on app vs general workspace)
  const hasApp = Boolean(appName)
  const [panelPrefs, setPanelPrefs] = useState<PanelType[]>(() => loadPanelPrefs(hasApp))
  const activePanels = useMemo(() => new Set(panelPrefs), [panelPrefs])
  const availablePanels = useMemo(() => {
    const panels: PanelType[] = ['preview', 'browser', 'files']
    if (appName) {
      panels.push('docs')
    }
    return panels
  }, [appName])

  // Ensure workspace is ready and user has edit permission
  useEffect(() => {
    let mounted = true

    async function checkWorkspace() {
      try {
        setWorkspaceStatus('loading')

        // Check if user has edit permission on the app
        if (appName) {
          try {
            const app = await getApp(appName)
            const myPermission = app.my_permission ?? 'owner'
            const canEdit = ['owner', 'admin', 'edit'].includes(myPermission)

            if (!canEdit) {
              // View-only users should be redirected to the app URL
              if (app.url) {
                window.location.href = app.url
                return
              }
              // If no URL available, navigate to apps page
              navigate('/apps')
              return
            }
          } catch (err) {
            // If we can't fetch the app, likely unauthorized
            console.error('Failed to verify app permission:', err)
            if (mounted) {
              setError('You do not have permission to access this workspace')
              setWorkspaceStatus('error')
            }
            return
          }
        }

        const ws = await ensureWorkspace()
        if (mounted) {
          setWorkspace(ws)
          setWorkspaceStatus('ready')
        }
      } catch (err) {
        // Nested imported platforms may not expose the global /api/workspace
        // bootstrap endpoint, but app-scoped workspace routes can still work.
        if (appName && err instanceof Error && err.message.toLowerCase().includes('not found')) {
          if (mounted) {
            setWorkspace(null)
            setWorkspaceStatus('ready')
          }
          return
        }
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to initialize workspace')
          setWorkspaceStatus('error')
        }
      }
    }

    checkWorkspace()

    return () => {
      mounted = false
    }
  }, [appName, navigate])

  // Poll import status (when workspace is opened from import flow)
  useEffect(() => {
    if (!importId) {
      setImportProgress(null)
      setImportEvents([])
      return
    }

    let cancelled = false
    let interval: ReturnType<typeof setInterval> | null = null

    const poll = async () => {
      try {
        const status = await getImportStatus(importId)
        let timelineEvents: ImportTimelineEvent[] = []
        try {
          const timeline = await getImportTimeline(importId, 40)
          timelineEvents = timeline.events
        } catch {
          // Keep status polling resilient even if timeline call fails.
        }
        if (cancelled) return
        setImportProgress(status)
        setImportEvents(timelineEvents)
        if (status.status === 'complete' || status.status === 'failed') {
          if (interval) {
            clearInterval(interval)
            interval = null
          }
        }
      } catch {
        // Keep polling on transient fetch errors
      }
    }

    void poll()
    interval = setInterval(() => {
      void poll()
    }, 2000)

    return () => {
      cancelled = true
      if (interval) {
        clearInterval(interval)
      }
    }
  }, [importId])

  const importBanner = useMemo(() => {
    if (!importId || !importProgress) return null
    if (dismissedImportBannerId === importId) return null

    const status = importProgress.status
    const isFailed = status === 'failed'
    const isComplete = status === 'complete'
    const isInProgress = !isFailed && !isComplete

    let title = 'Import in progress'
    let detail = 'Preparing workspace and project files'
    if (status === 'cloning') {
      title = 'Cloning repository'
      detail = 'Fetching source code into the workspace'
    } else if (status === 'converting') {
      title = 'Converting codebase'
      detail = 'Generating runtime and deployment config'
    } else if (status === 'deploying') {
      title = 'Deploying app'
      detail = 'Building and verifying app startup'
    } else if (status === 'uploading') {
      title = 'Uploading project'
      detail = 'Receiving archive and extracting files'
    } else if (isComplete) {
      title = 'Import complete'
      detail = 'App is ready. You can continue from this workspace.'
    } else if (isFailed) {
      title = 'Import failed'
      detail = importProgress.error_message || 'Open logs in terminal and retry conversion or deployment.'
    }

    const containerClass = isFailed
      ? 'border-destructive/20 bg-destructive/10'
      : isComplete
        ? 'border-green-500/20 bg-green-500/10'
        : 'border-primary/20 bg-primary/5'
    const titleClass = isFailed
      ? 'text-destructive'
      : isComplete
        ? 'text-green-700 dark:text-green-400'
        : 'text-foreground'

    return (
      <div className={`px-4 py-2 border-b ${containerClass}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {isInProgress && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
              <p className={`text-sm font-medium ${titleClass}`}>{title}</p>
            </div>
            <p className="text-xs text-muted-foreground">{detail}</p>
          </div>
          <button
            type="button"
            className="mt-0.5 rounded p-1 text-muted-foreground hover:text-foreground hover:bg-background/60 transition-colors"
            onClick={() => setDismissedImportBannerId(importId)}
            aria-label="Dismiss import status banner"
            title="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    )
  }, [dismissedImportBannerId, importId, importProgress])

  // Handle panel toggle
  const handlePanelToggle = useCallback((panel: PanelType) => {
    setPanelPrefs((prev) => {
      const isActive = prev.includes(panel)
      const newPanels = isActive
        ? prev.filter((p) => p !== panel)
        : [...prev, panel]
      savePanelPrefs(newPanels, hasApp)
      return newPanels
    })
  }, [hasApp])

  // Handle panel close (from within the panel)
  const handlePanelClose = useCallback((panel: PanelType) => {
    handlePanelToggle(panel)
  }, [handlePanelToggle])

  // Handle container restart
  const handleRestart = useCallback(async () => {
    if (!appName) return

    setRestarting(true)
    try {
      const result = await restartAppWorkspaceContainer(appName)
      if (result.status === 'restarted') {
        // Reload the page to reconnect to the restarted container
        window.location.reload()
        return
      }

      // For cooldown/in-progress no-op responses, keep current page/session.
      setRestarting(false)
    } catch (err) {
      console.error('Failed to restart container:', err)
      setRestarting(false)
      throw err // Re-throw so ConfirmDialog shows error state
    }
  }, [appName])

  // Loading state
  if (workspaceStatus === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Starting workspace...</p>
      </div>
    )
  }

  // Error state
  if (workspaceStatus === 'error') {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-background">
        <p className="text-destructive mb-4">{error || 'Failed to start workspace'}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Toolbar */}
      <div className="sticky top-0 z-40 flex items-center justify-between px-4 py-2 border-b border-border bg-card flex-shrink-0 workspace-header-mobile">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-1.5 rounded hover:bg-secondary transition-colors"
            title="Back to home"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Workspace</span>
            <BetaBadge compact />
            {appName && (
              <>
                <span className="text-muted-foreground">/</span>
                <span className="text-sm font-medium text-primary">{appName}</span>
              </>
            )}
            {appName && (
              <>
                <span className="text-muted-foreground">/</span>
                <SessionDropdown
                  appName={appName}
                  activeSession={activeSession}
                  onSelectSession={handleSelectSession}
                />
              </>
            )}
          </div>
          {appName && (
            <ConfirmDialog
              trigger={
                <button
                  disabled={restarting}
                  className="p-1.5 rounded hover:bg-secondary transition-colors disabled:opacity-50"
                  title="Restart container"
                >
                  <RotateCw className={`w-4 h-4 ${restarting ? 'animate-spin' : ''}`} />
                </button>
              }
              title="Restart Workspace Container"
              description="This will restart the workspace container and interrupt any running processes. The page will reload automatically."
              confirmLabel="Restart"
              variant="destructive"
              onConfirm={handleRestart}
            />
          )}
        </div>

        {/* Desktop: Panel toggles */}
        {!isMobile && (
          <PanelToggle
            activePanels={activePanels}
            availablePanels={availablePanels}
            onToggle={handlePanelToggle}
          />
        )}

        {/* Mobile: Menu button for panel controls */}
        {isMobile && availablePanels.length > 0 && (
          <div className="relative">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-1.5 rounded hover:bg-secondary transition-colors"
              title="Panel options"
            >
              <Menu className="w-4 h-4" />
            </button>
            {mobileMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setMobileMenuOpen(false)}
                />
                <div className="absolute right-0 top-full mt-1 z-50 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[140px]">
                  {availablePanels.map((panel) => (
                    <button
                      key={panel}
                      onClick={() => {
                        handlePanelToggle(panel)
                        setMobileMenuOpen(false)
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-secondary transition-colors flex items-center justify-between"
                    >
                      <span className="capitalize">{panel}</span>
                      {activePanels.has(panel) && (
                        <span className="text-primary">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {importBanner}

      {/* Workspace content */}
      <div className="flex-1 overflow-hidden">
        {isMobile ? (
          <MobileWorkspaceLayout
            activePanels={activePanels}
            onPanelClose={handlePanelClose}
            cwd={folder || undefined}
            browserUrl={effectivePreviewUrl || undefined}
            appName={appName || undefined}
            tmuxSession={tmuxSession || undefined}
            requestedSession={requestedSession}
            onSessionChange={handleSessionChange}
          />
        ) : (
          <WorkspaceLayout
            activePanels={activePanels}
            onPanelClose={handlePanelClose}
            cwd={folder || undefined}
            browserUrl={effectivePreviewUrl || undefined}
            appName={appName || undefined}
            tmuxSession={tmuxSession || undefined}
            requestedSession={requestedSession}
            onSessionChange={handleSessionChange}
          />
        )}
      </div>
      <HelpDrawer />
    </div>
  )
}
